import { useCallback, useEffect, useRef, useState } from 'react';
import { PaginatedDataHook, setupDataFetcher } from './setupDataFetcher';

export interface Balance {
  total: string;
  available: string;
}

export interface Funds {
  id: string;
  asset: string;
  freezes: {
    id: string;
    amount: string;
  }[];
  balance: Balance;
}

export type UseFundsFetcher = PaginatedDataHook<Funds[]>;
export type UseAssetFundsFetcher = PaginatedDataHook<Funds>;

export function useFunds(
  { accountId, asset }: { accountId?: string; asset?: string },
  refreshInterval: number = 30000
): UseFundsFetcher {
  const [data, setData] = useState<Funds[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const fetchPageByUrl = useRef<any>(null);
  const disposer = useRef(() => {});

  const setupFundsFetcher = useCallback(() => {
    setData([]);
    setLoading(true);
    setError(false);
    setNextPageUrl(null);
    setLoadingNextPage(false);

    if (!accountId) return;

    const onResult = message => {
      setData(message.body);
      setNextPageUrl(message.nextPageUrl ?? null);
      setLoading(false);
      setError(false);
    };
    const onPaginatedResult = (message, existingData) => {
      setData([...existingData, ...message.body]);
      setNextPageUrl(message.nextPageUrl ?? null);
      setLoadingNextPage(false);
    };
    const onError = message => {
      console.error(message);
      setError(true);
      setLoading(false);
      disposer.current?.();
    };
    function generateFetchKey({
      asset = undefined,
      accountId = undefined,
      nextPageUrl: nextPageUrlParam = undefined
    } = {}) {
      if (asset && accountId) return `funds-${asset}-${accountId}`;
      if (accountId && nextPageUrlParam) return `funds-${accountId}-${nextPageUrlParam}`;
      if (accountId) return `funds-${accountId}`;
      return `funds`;
    }

    const setupResult = setupDataFetcher({
      onResult,
      onPaginatedResult,
      onError,
      generateFetchKey,
      refreshInterval,
      propParams: {
        accountId,
        asset
      }
    });
    disposer.current = setupResult.disposer;
    fetchPageByUrl.current = setupResult.fetchPageByUrl;
  }, [accountId, asset]);

  useEffect(() => {
    setupFundsFetcher();
    return () => disposer.current?.();
  }, [setupFundsFetcher]);

  const refetch = () => {
    disposer.current?.();
    setupFundsFetcher();
  };

  const fetchNextPage = () => {
    if (loading || loadingNextPage || nextPageUrl === null) return;
    setLoadingNextPage(true);
    fetchPageByUrl.current(data, nextPageUrl);
  };

  return {
    data,
    loading,
    error,
    refetch,
    hasNextPage: nextPageUrl !== null,
    loadingNextPage,
    fetchNextPage
  };
}

export function useAssetFunds(
  { accountId, asset }: { accountId?: string; asset?: string },
  refreshInterval: number = 30000
): UseAssetFundsFetcher {
  const [data, setData] = useState<Funds>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const fetchPageByUrl = useRef<any>(null);
  const disposer = useRef(() => {});

  const setupFundsFetcher = useCallback(() => {
    setData(undefined);
    setLoading(true);
    setError(false);
    setNextPageUrl(null);
    setLoadingNextPage(false);

    if (!accountId) return;

    const onResult = message => {
      setData(message);
      setNextPageUrl(message.nextPageUrl ?? null);
      setLoading(false);
      setError(false);
    };
    const onPaginatedResult = message => {
      setData({ ...message.body });
      setNextPageUrl(message.nextPageUrl ?? null);
      setLoadingNextPage(false);
    };
    const onError = message => {
      console.error(message);
      setError(true);
      setLoading(false);
      disposer.current?.();
    };
    function generateFetchKey({
      asset = undefined,
      accountId = undefined,
      nextPageUrl: nextPageUrlParam = undefined
    } = {}) {
      if (asset && accountId) return `funds-${asset}-${accountId}`;
      if (accountId && nextPageUrlParam) return `funds-${accountId}-${nextPageUrlParam}`;
      if (accountId) return `funds-${accountId}`;
      return `funds`;
    }

    const setupResult = setupDataFetcher({
      onResult,
      onPaginatedResult,
      onError,
      generateFetchKey,
      refreshInterval,
      propParams: {
        accountId,
        asset
      }
    });
    disposer.current = setupResult.disposer;
    fetchPageByUrl.current = setupResult.fetchPageByUrl;
  }, [accountId, asset]);

  useEffect(() => {
    setupFundsFetcher();
    return () => disposer.current?.();
  }, [setupFundsFetcher]);

  const refetch = () => {
    disposer.current?.();
    setupFundsFetcher();
  };

  const fetchNextPage = () => {
    if (loading || loadingNextPage || nextPageUrl === null) return;
    setLoadingNextPage(true);
    fetchPageByUrl.current(data, nextPageUrl);
  };

  return {
    data,
    loading,
    error,
    refetch,
    hasNextPage: nextPageUrl !== null,
    loadingNextPage,
    fetchNextPage
  } as unknown as UseAssetFundsFetcher;
}
